import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import SessionWarningModal from "../components/session/SessionWarningModal";
import ApiUnreachableModal from "../components/session/ApiUnreachableModal";
import { SESSION_KEEPALIVE_ENABLED } from "../config/sessionKeepAlive";
import { authAPI, getApiBaseUrl } from "../services/api";
import { logSessionEvent } from "../services/apiClientLog";
import { useSessionHeartbeat } from "../hooks/useSessionHeartbeat";
import {
  SESSION_WARNING_BEFORE_MS,
  extendSession,
  extendSessionFromServer,
  getMsUntilSessionExpiry,
  getSessionExpiresAt,
  getUser,
  isAuthenticated,
  logout,
  touchSession,
} from "../utils/auth";

const SessionContext = createContext({
  sessionActive: false,
  refreshSession: async () => {},
  forceLogout: () => {},
});

export function useSession() {
  return useContext(SessionContext);
}

function getUserIdCodeFromStorage() {
  const user = getUser();
  if (!user) return null;
  return (
    user.userIdCode ||
    user.UserIdCode ||
    user.userId ||
    user.UserId ||
    null
  );
}

/** Renew server cookie via a normal authenticated call (keep-alive endpoint is off). */
async function renewSessionWithoutKeepAlive() {
  extendSession();
  const code = getUserIdCodeFromStorage();
  if (!code) return;
  await authAPI.getUser(code);
}

/**
 * Enterprise session manager: expiry warning, 401 handling, optional keep-alive heartbeat.
 */
export function SessionProvider({ children, enabled = true }) {
  const [warningOpen, setWarningOpen] = useState(false);
  const [apiUnreachableOpen, setApiUnreachableOpen] = useState(false);
  const [continuing, setContinuing] = useState(false);
  const warningShownRef = useRef(false);
  const keepAliveFailStreakRef = useRef(0);
  const loggingOutRef = useRef(false);
  const expiryTimerRef = useRef(null);
  const warningTimerRef = useRef(null);

  const clearTimers = useCallback(() => {
    if (expiryTimerRef.current != null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (warningTimerRef.current != null) {
      window.clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const handleSessionExpired = useCallback(async () => {
    if (loggingOutRef.current) return;
    loggingOutRef.current = true;
    clearTimers();
    setWarningOpen(false);
    warningShownRef.current = false;
    logSessionEvent("session-end", "expired", getApiBaseUrl);
    try {
      await authAPI.logout();
    } catch {
      // cookie may already be gone
    }
    logout();
  }, [clearTimers]);

  const scheduleExpiryTimers = useCallback(() => {
    clearTimers();
    if (!enabled || !isAuthenticated()) return;

    let msLeft = getMsUntilSessionExpiry();
    // Legacy logins may have user in storage but no expiry key — initialize, do not force logout.
    if (msLeft == null) {
      touchSession();
      msLeft = getMsUntilSessionExpiry();
    }
    if (msLeft == null || msLeft <= 0) {
      handleSessionExpired();
      return;
    }

    const warnIn = Math.max(0, msLeft - SESSION_WARNING_BEFORE_MS);
    if (!warningShownRef.current && warnIn >= 0) {
      warningTimerRef.current = window.setTimeout(() => {
        if (isAuthenticated()) {
          warningShownRef.current = true;
          setWarningOpen(true);
        }
      }, warnIn);
    }

    expiryTimerRef.current = window.setTimeout(() => {
      if (isAuthenticated()) {
        handleSessionExpired();
      }
    }, msLeft);
  }, [clearTimers, enabled, handleSessionExpired]);

  const onKeepAliveSuccess = useCallback(
    (payload) => {
      keepAliveFailStreakRef.current = 0;
      setApiUnreachableOpen(false);
      const sec = payload?.sessionExpiresIn ?? payload?.SessionExpiresIn;
      if (typeof sec === "number") {
        extendSessionFromServer(sec);
      }
      warningShownRef.current = false;
      setWarningOpen(false);
      scheduleExpiryTimers();
    },
    [scheduleExpiryTimers]
  );

  const onKeepAliveFailure = useCallback((err) => {
    const canceled =
      err?.code === "ERR_CANCELED" ||
      err?.name === "CanceledError" ||
      err?.name === "AbortError" ||
      String(err?.message || "")
        .toLowerCase()
        .includes("canceled") ||
      String(err?.message || "")
        .toLowerCase()
        .includes("aborted");
    if (canceled) return;

    keepAliveFailStreakRef.current += 1;
    const isNetwork =
      err?.code === "ERR_NETWORK" ||
      err?.message?.includes("Network Error") ||
      (err?.response == null && err?.code !== "ERR_CANCELED");
    if (isNetwork || keepAliveFailStreakRef.current >= 2) {
      setApiUnreachableOpen(true);
    }
  }, []);

  const { runKeepAlive } = useSessionHeartbeat({
    enabled: SESSION_KEEPALIVE_ENABLED && enabled && isAuthenticated(),
    onKeepAliveSuccess,
    onSessionExpired: handleSessionExpired,
    onKeepAliveFailure,
  });

  const refreshSession = useCallback(async () => {
    if (SESSION_KEEPALIVE_ENABLED) {
      await runKeepAlive();
    } else {
      try {
        await renewSessionWithoutKeepAlive();
      } catch (err) {
        if (err?.response?.status === 401 || err?.isUnauthorized) {
          await handleSessionExpired();
          return;
        }
        throw err;
      }
    }
    scheduleExpiryTimers();
  }, [runKeepAlive, scheduleExpiryTimers, handleSessionExpired]);

  const handleContinueSession = useCallback(async () => {
    setContinuing(true);
    try {
      await refreshSession();
    } finally {
      setContinuing(false);
      setWarningOpen(false);
      warningShownRef.current = false;
    }
  }, [refreshSession]);

  const handleLogout = useCallback(async () => {
    setWarningOpen(false);
    await handleSessionExpired();
  }, [handleSessionExpired]);

  useEffect(() => {
    if (!enabled) return undefined;

    if (isAuthenticated() && getSessionExpiresAt() == null) {
      touchSession();
    }
    scheduleExpiryTimers();

    const onUnauthorized = () => {
      handleSessionExpired();
    };

    window.addEventListener("csrp:unauthorized", onUnauthorized);
    window.addEventListener("csrp:session-expired", onUnauthorized);

    return () => {
      clearTimers();
      window.removeEventListener("csrp:unauthorized", onUnauthorized);
      window.removeEventListener("csrp:session-expired", onUnauthorized);
    };
  }, [enabled, scheduleExpiryTimers, clearTimers, handleSessionExpired]);

  const value = useMemo(
    () => ({
      sessionActive: enabled && isAuthenticated(),
      refreshSession,
      forceLogout: handleSessionExpired,
    }),
    [enabled, refreshSession, handleSessionExpired]
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      {enabled && (
        <>
          <SessionWarningModal
            open={warningOpen}
            onContinue={handleContinueSession}
            onLogout={handleLogout}
            continuing={continuing}
          />
          {SESSION_KEEPALIVE_ENABLED && (
            <ApiUnreachableModal
              open={apiUnreachableOpen}
              onReload={() => window.location.reload()}
              onLogout={handleLogout}
            />
          )}
        </>
      )}
    </SessionContext.Provider>
  );
}

export default SessionContext;
